import { observer } from 'mobx-react-lite'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import type { TaxReturnYearModalViewModel } from '@/features/jobs/details/tax-type/TaxReturnYearModalViewModel'
import type { ReturnAmountsYear } from '@/features/jobs/job-type'
import {
  type TaxFilingOutcome,
  TaxReturnAmountsSection,
  TaxReturnYearAmountListItem,
  TaxReturnYearOutcomeModal,
  TaxReturnYearOutcomeModalStateReturnItem,
} from '@/features/jobs/details/tax-type/components/TaxReturnAmounts/index'

interface TaxReturnAmountsSectionContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Container for the tax return amounts section, including the
 * tax return year outcome modal.
 */
export const TaxReturnAmountsSectionContainer = observer(
  function TaxReturnAmountsSectionContainer({
    vm,
  }: TaxReturnAmountsSectionContainerProps) {
    const years = vm.type.returnAmounts

    if (!years.length) {
      return null
    }

    return (
      <>
        <TaxReturnAmountsSection outcome={getSectionOutcome(years)}>
          {years.map((year) => (
            <TaxReturnYearAmountListItem
              key={year.taxYear}
              taxYear={year.taxYear}
              totalBalanceUsd={getYearTotal(year)}
              stateCodes={year.stateAmounts.map((state) => state.state)}
              onAction={() => vm.taxReturnYearModalVm.show(year)}
            />
          ))}
        </TaxReturnAmountsSection>
        <TaxReturnYearOutcomeModalContainer vm={vm.taxReturnYearModalVm} />
      </>
    )
  },
)

interface TaxReturnYearOutcomeModalContainerProps {
  vm: TaxReturnYearModalViewModel
}

/**
 * Container for the tax return year outcome modal.
 */
const TaxReturnYearOutcomeModalContainer = observer(
  function TaxReturnYearOutcomeModalContainer({
    vm,
  }: TaxReturnYearOutcomeModalContainerProps) {
    const year = vm.taxReturnYear

    return (
      <TaxReturnYearOutcomeModal
        isOpen={vm.modal.showing}
        onClose={vm.modal.close}
        taxYear={year?.taxYear ?? ''}
        federalBalanceUsd={year?.federalAmount ?? 0}
      >
        {year?.stateAmounts.map((state) => (
          <TaxReturnYearOutcomeModalStateReturnItem
            key={state.state}
            stateCode={state.state}
            stateBalanceUsd={state.amount}
          />
        ))}
      </TaxReturnYearOutcomeModal>
    )
  },
)

/**
 * Total net balance for a tax year (federal plus all state returns).
 */
function getYearTotal(year: ReturnAmountsYear) {
  return year.stateAmounts.reduce(
    (total, state) => total + state.amount,
    year.federalAmount,
  )
}

/**
 * Derives the overall filing outcome across all tax years.
 */
function getSectionOutcome(
  years: readonly ReturnAmountsYear[],
): TaxFilingOutcome {
  let hasRefund = false
  let hasBalanceDue = false

  for (const year of years) {
    const total = getYearTotal(year)
    if (total < 0) {
      hasBalanceDue = true
    } else if (total > 0) {
      hasRefund = true
    }
  }

  if (hasRefund && hasBalanceDue) {
    return 'both'
  }

  return hasBalanceDue ? 'balance-due' : 'refund'
}
